import { useEffectEvent, useLayoutEffect, useRef } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { getJ5ArtifactDownloadQueryOptions } from '@interxfi/api-client/registry/download-artifact';
import {
  TableOfContentsContextProvider,
  useTableOfContentsContext,
} from '@interxfi/design-system/components/table-of-contents/table-of-contents-context';
import { getScrollParent } from '@interxfi/design-system/util';
import type { J5RegistryV1ArtifactState } from '@interxfi/types';
import { GuideLayout } from '../../../../../features/docs/guide-layout/guide-layout';

export const Route = createFileRoute('/docs/_layout/guide/_provider/$artifactName')({
  component: ApiDocsGuidePage,
  beforeLoad: async ({ context, params }) => {
    if (!context.apiGuidesData) {
      throw redirect({ to: '/docs', search: true });
    }

    let matchingGuide: J5RegistryV1ArtifactState | undefined;
    let nextGuide: J5RegistryV1ArtifactState | undefined;

    for (const page of context.apiGuidesData.pages || []) {
      for (const guide of page?.artifact || []) {
        if (guide.name === params.artifactName) {
          matchingGuide = guide;
        }

        if (matchingGuide && guide.name !== matchingGuide.name) {
          nextGuide = guide;
          break;
        }
      }

      if (nextGuide) {
        break;
      }
    }

    if (!matchingGuide) {
      throw redirect({ to: '/docs/guide', search: true });
    }

    return {
      // TODO: return "download" here
      guideData: matchingGuide,
      nextGuide,
    } as const;
  },
  loader: async ({ context }) => {
    const markdown = (await context.queryClient.ensureQueryData(
      getJ5ArtifactDownloadQueryOptions({
        owner: context.guideData.owner,
        bundle: context.guideData.bundle,
        version: context.guideData.version,
        name: context.guideData.name,
      }),
    )) as string;

    return { markdown, nextGuide: context.nextGuide } as const;
  },
  // TODO: move redirect logic to loader and allow pre-load
  preload: false,
});

function InnerApiDocsGuidePage() {
  const ref = useRef<HTMLDivElement>(null);
  const { artifactName } = Route.useParams();
  const { markdown, nextGuide } = Route.useLoaderData();
  const { getTableOfContentsSentries, isProgrammaticScrollRef, setActiveTab } = useTableOfContentsContext();

  const resetActiveTab = useEffectEvent(() => {
    const sentries = getTableOfContentsSentries();

    if (sentries.length > 0) {
      setActiveTab(sentries[0].id);
    }
  });

  useLayoutEffect(() => {
    if (artifactName) {
      const scrollParent = getScrollParent(ref?.current);

      if (scrollParent) {
        function handleScrollEnd() {
          isProgrammaticScrollRef.current = false;
          scrollParent?.removeEventListener('scrollend', handleScrollEnd);
          resetActiveTab();
        }

        isProgrammaticScrollRef.current = true;

        scrollParent.scrollTo({ top: 0, behavior: 'instant' });

        scrollParent.addEventListener('scrollend', handleScrollEnd);
      }
    }
  }, [artifactName, isProgrammaticScrollRef]);

  return <GuideLayout markdown={markdown} nextGuide={nextGuide} ref={ref} />;
}

function ApiDocsGuidePage() {
  return (
    <TableOfContentsContextProvider>
      <InnerApiDocsGuidePage />
    </TableOfContentsContextProvider>
  );
}
